import { Routes, Route } from 'react-router-dom'
import { AuthProvider } from './contexts/AuthContext'
import { CartProvider } from './contexts/CartContext'
import { StoreProvider } from './contexts/StoreContext'
import { StoreDetailProvider } from './contexts/StoreDetailContext'
import { CacheProvider } from './contexts/CacheContext'
import Navbar from './components/Navbar'
import ProtectedRoute from './components/ProtectedRoute'
import PublicRoute from './components/PublicRoute'

// Pages
import Home from './pages/Home'
import StoreSelection from './pages/StoreSelection'
import StoreProfile from './pages/StoreProfile'
import Catalog from './pages/Catalog'
import Cart from './pages/Cart'
import Checkout from './pages/Checkout'
import OrderHistory from './pages/OrderHistory'
import OrderDetails from './pages/OrderDetails'
import Profile from './pages/Profile'
import Login from './pages/Login'
import Register from './pages/Register'

// Páginas de gestión de colmados (otros roles)
import KitchenOrders from './pages/store/KitchenOrders'
import DeliveryDashboard from './pages/store/DeliveryDashboard'
import DeliveryOrderDetails from './pages/store/DeliveryOrderDetails'
import DeliveryCompleteOrder from './pages/store/DeliveryCompleteOrder'
import DeliveryWallet from './pages/store/DeliveryWallet'

// Páginas del despachador (dispatcher) - Flujo independiente
import DispatcherDashboard from './pages/dispatcher/dashboard/DispatcherDashboard'
import DispatcherOrderDetails from './pages/dispatcher/orders/DispatcherOrderDetails'
import DispatcherPOS from './pages/dispatcher/pos/DispatcherPOS'

// Páginas de dueños de colmados (store_owner) - Organizadas en carpeta separada
import StoreOwnerDashboard from './pages/store-owner/dashboard/StoreOwnerDashboard'
import CreateStore from './pages/store-owner/stores/CreateStore'
import MyStores from './pages/store-owner/stores/MyStores'
import RequestStoreOwner from './pages/store-owner/stores/RequestStoreOwner'
import StoreDashboard from './pages/store-owner/management/StoreDashboard'
import StoreProducts from './pages/store-owner/management/StoreProducts'
import StoreCategories from './pages/store-owner/management/StoreCategories'
import StoreOrders from './pages/store-owner/management/StoreOrders'
import StoreOrderDetails from './pages/store-owner/management/StoreOrderDetails'
import StoreEmployees from './pages/store-owner/management/StoreEmployees'
import EmployeeDetails from './pages/store-owner/management/EmployeeDetails'
import StoreSettings from './pages/store-owner/management/StoreSettings'
import EditProduct from './pages/store-owner/management/EditProduct'
import ViewProduct from './pages/store-owner/management/ViewProduct'
import StoreCashRegister from './pages/store-owner/management/StoreCashRegister'
import ManageDeliveryWallets from './pages/store-owner/management/ManageDeliveryWallets'
import InventoryScanner from './pages/store-owner/management/InventoryScanner'

// Menu pages
import MenuScreen from './pages/MenuScreen'

// Components
import BottomNav from './components/BottomNav'
import Footer from './components/Footer'
import CookieConsentBanner from './components/CookieConsentBanner'

// Admin pages
import AdminDashboard from './pages/admin/AdminDashboard'
import AdminStoreApproval from './pages/admin/AdminStoreApproval'
import AdminStoreDetails from './pages/admin/AdminStoreDetails'
import AdminOrders from './pages/admin/AdminOrders'
import AdminSystemStatus from './pages/admin/AdminSystemStatus'
import StoreOwnerRequests from './pages/admin/StoreOwnerRequests'
import AdminUserManagement from './pages/admin/AdminUserManagement'
import AdminCategories from './pages/admin/AdminCategories'
import AdminStoreOwners from './pages/admin/AdminStoreOwners'
import AdminNews from './pages/admin/AdminNews'
import AdminOrderDetails from './pages/admin/AdminOrderDetails'
import AdminCurrencyDenominations from './pages/admin/AdminCurrencyDenominations'
import StoreOwnerDetails from './pages/admin/StoreOwnerDetails'
import UserDetails from './pages/admin/UserDetails'
import AdminPlatformSettings from './pages/admin/AdminPlatformSettings'
import NewsPage from './pages/NewsPage'
import JoinStorePage from './pages/JoinStorePage'
import ProductDetail from './pages/ProductDetail'
import TermsAndConditions from './pages/TermsAndConditions'
import PrivacyPolicy from './pages/PrivacyPolicy'
import Contact from './pages/Contact'
import HelpCenter from './pages/HelpCenter'

function App() {
  return (
    <AuthProvider>
      <CacheProvider>
        <StoreProvider>
          <StoreDetailProvider>
            <CartProvider>
          <div className="min-h-screen bg-gray-50">
            {/* Navbar only for desktop (lg and up) */}
            <div className="hidden lg:block">
              <Navbar />
            </div>
            <main className="lg:pt-16 pt-0 pb-20 lg:pb-0 flex flex-col min-h-screen">
              <div className="flex-1">
              <Routes>
                {/* Public Routes */}
                <Route path="/" element={<Home />} />
                <Route path="/stores" element={<StoreSelection />} />
                <Route path="/news" element={<NewsPage />} />
                <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
                <Route path="/register" element={<PublicRoute><Register /></PublicRoute>} />
                <Route path="/terms" element={<TermsAndConditions />} />
                <Route path="/privacy" element={<PrivacyPolicy />} />
                <Route path="/contact" element={<Contact />} />
                <Route path="/help" element={<HelpCenter />} />

                {/* Public Routes - Stores and Products */}
                <Route path="/stores/:storeId" element={<StoreProfile />} />
                <Route path="/stores/:storeId/catalog" element={<Catalog />} />
                <Route path="/stores/:storeId/product" element={<ProductDetail />} />
                <Route path="/stores/:storeId/cart" element={<Cart />} />
                
                {/* Customer Routes - Require Authentication (accessible to customers and employees in customer mode) */}
                <Route path="/cart" element={<Cart />} />
                <Route path="/checkout/:storeId" element={<Checkout />} />
                <Route path="/orders" element={<OrderHistory />} />
                <Route path="/orders/:orderId" element={<OrderDetails />} />
                <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
                <Route path="/request-store-owner" element={<ProtectedRoute><RequestStoreOwner /></ProtectedRoute>} />
                <Route path="/join-store" element={<ProtectedRoute><JoinStorePage /></ProtectedRoute>} />

                {/* Menu Routes */}
                <Route path="/menu" element={<MenuScreen />} />

                {/* Store Owner Routes */}
                <Route path="/store-owner/dashboard" element={<StoreOwnerDashboard />} />
                <Route path="/store-owner/my-stores" element={<MyStores />} />
                <Route path="/create-store" element={<CreateStore />} />

                {/* Store Management Routes */}
                <Route path="/store/dashboard" element={<ProtectedRoute allowedRoles={['store_owner', 'store_admin']}><StoreDashboard /></ProtectedRoute>} />
                {/* Delivery routes - Protected for delivery role only */}
                <Route path="/store/delivery" element={<ProtectedRoute requiredRole="delivery"><DeliveryDashboard /></ProtectedRoute>} />
                <Route path="/store/delivery/wallet" element={<ProtectedRoute requiredRole="delivery"><DeliveryWallet /></ProtectedRoute>} />
                <Route path="/store/delivery/orders/:orderId" element={<ProtectedRoute requiredRole="delivery"><DeliveryOrderDetails /></ProtectedRoute>} />
                <Route path="/store/delivery/orders/:orderId/complete" element={<ProtectedRoute requiredRole="delivery"><DeliveryCompleteOrder /></ProtectedRoute>} />
                <Route path="/store/:storeId/employees/:employeeId" element={<ProtectedRoute requiredRole="store_owner"><EmployeeDetails /></ProtectedRoute>} />
                <Route path="/store/:storeId/dashboard" element={<ProtectedRoute allowedRoles={['store_owner', 'store_admin']}><StoreDashboard /></ProtectedRoute>} />
                <Route path="/store/:storeId/products" element={<StoreProducts />} />
                <Route path="/store/:storeId/products/:productId" element={<ViewProduct />} />
                <Route path="/store/:storeId/products/:productId/edit" element={<EditProduct />} />
                <Route path="/store/:storeId/inventory" element={<InventoryScanner />} />
                <Route path="/store/inventory" element={<InventoryScanner />} />
                <Route path="/store/:storeId/categories" element={<StoreCategories />} />
                <Route path="/store/categories" element={<StoreCategories />} />
                <Route path="/store/:storeId/cash-register" element={<StoreCashRegister />} />
                <Route path="/store/cash-register" element={<StoreCashRegister />} />
                <Route path="/store/:storeId/delivery-wallets" element={<ManageDeliveryWallets />} />
                <Route path="/store/delivery-wallets" element={<ManageDeliveryWallets />} />
                <Route path="/store/orders" element={<StoreOrders />} />
                <Route path="/store/:storeId/orders" element={<StoreOrders />} />
                <Route path="/store/:storeId/orders/:orderId" element={<StoreOrderDetails />} />
                <Route path="/store/:storeId/employees" element={<StoreEmployees />} />
                <Route path="/store/:storeId/employees/:employeeId" element={<ProtectedRoute requiredRole="store_owner"><EmployeeDetails /></ProtectedRoute>} />
                <Route path="/store/:storeId/settings" element={<StoreSettings />} />
                <Route path="/store/:storeId/kitchen" element={<KitchenOrders />} />
                
                {/* Dispatcher Routes - Flujo independiente */}
                <Route path="/dispatcher" element={<ProtectedRoute requiredRole="dispatcher"><DispatcherDashboard /></ProtectedRoute>} />
                <Route path="/dispatcher/orders/:orderId" element={<ProtectedRoute requiredRole="dispatcher"><DispatcherOrderDetails /></ProtectedRoute>} />
                <Route path="/dispatcher/pos" element={<ProtectedRoute requiredRole="dispatcher"><DispatcherPOS /></ProtectedRoute>} />
                <Route path="/store/:storeId/dispatch" element={<ProtectedRoute requiredRole="dispatcher"><DispatcherDashboard /></ProtectedRoute>} />
                <Route path="/store/dashboard" element={<ProtectedRoute requiredRole="dispatcher"><DispatcherDashboard /></ProtectedRoute>} />
                <Route path="/store/:storeId/delivery" element={<ProtectedRoute requiredRole="delivery"><DeliveryDashboard /></ProtectedRoute>} />

                {/* Admin Routes - Protected */}
                <Route path="/admin/dashboard" element={<ProtectedRoute requiredRole="platform_admin"><AdminDashboard /></ProtectedRoute>} />
                <Route path="/admin/system-status" element={<ProtectedRoute requiredRole="platform_admin"><AdminSystemStatus /></ProtectedRoute>} />
                <Route path="/admin/stores" element={<ProtectedRoute requiredRole="platform_admin"><AdminStoreApproval /></ProtectedRoute>} />
                <Route path="/admin/stores/:storeId" element={<ProtectedRoute requiredRole="platform_admin"><AdminStoreDetails /></ProtectedRoute>} />
                <Route path="/admin/orders" element={<ProtectedRoute requiredRole="platform_admin"><AdminOrders /></ProtectedRoute>} />
                <Route path="/admin/orders/:orderId" element={<ProtectedRoute requiredRole="platform_admin"><AdminOrderDetails /></ProtectedRoute>} />
                <Route path="/admin/categories" element={<ProtectedRoute requiredRole="platform_admin"><AdminCategories /></ProtectedRoute>} />
                <Route path="/admin/news" element={<ProtectedRoute requiredRole="platform_admin"><AdminNews /></ProtectedRoute>} />
                <Route path="/admin/currency-denominations" element={<ProtectedRoute requiredRole="platform_admin"><AdminCurrencyDenominations /></ProtectedRoute>} />
                <Route path="/admin/store-owner-requests" element={<ProtectedRoute requiredRole="platform_admin"><StoreOwnerRequests /></ProtectedRoute>} />
                <Route path="/admin/users" element={<ProtectedRoute requiredRole="platform_admin"><AdminUserManagement /></ProtectedRoute>} />
                <Route path="/admin/users/:userId" element={<ProtectedRoute requiredRole="platform_admin"><UserDetails /></ProtectedRoute>} />
                <Route path="/admin/users/:userId/edit" element={<ProtectedRoute requiredRole="platform_admin"><UserDetails /></ProtectedRoute>} />
                <Route path="/admin/store-owners" element={<ProtectedRoute requiredRole="platform_admin"><AdminStoreOwners /></ProtectedRoute>} />
                <Route path="/admin/store-owners/:ownerId" element={<ProtectedRoute requiredRole="platform_admin"><StoreOwnerDetails /></ProtectedRoute>} />
                <Route path="/admin/store-owners/:ownerId/edit" element={<ProtectedRoute requiredRole="platform_admin"><StoreOwnerDetails /></ProtectedRoute>} />
                <Route path="/admin/platform-settings" element={<ProtectedRoute requiredRole="platform_admin"><AdminPlatformSettings /></ProtectedRoute>} />
              </Routes>
              </div>
              {/* Footer solo en desktop, en móvil se usa BottomNav */}
              <div className="hidden lg:block">
                <Footer />
              </div>
            </main>
            <BottomNav />
          </div>
            </CartProvider>
          </StoreDetailProvider>
        </StoreProvider>
      </CacheProvider>
    </AuthProvider>
  )
}

export default App
